import { useRef, useState } from "react";
import { ImagePlus, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, getToken, formatApiError } from "@/lib/api";

export default function LogoUpload({ logo, onChange, ideaId }) {
  const params = ideaId ? { idea: ideaId } : {};
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/organizer/logo", form, { headers: { "Content-Type": "multipart/form-data" }, params });
      onChange(data.logo);
      toast.success("Logo uploaded — it'll print on your PDF cover too.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete("/organizer/logo", { params });
      onChange(null);
      toast.success("Logo removed.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sm:col-span-2" data-testid="logo-upload">
      <label className="block text-xs font-bold uppercase tracking-wider text-[#8C7562] mb-2">Logo File</label>
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden"
        onChange={(e) => upload(e.target.files?.[0])} data-testid="logo-file-input" />
      {logo ? (
        <div className="flex flex-wrap items-center gap-5 rounded-xl border border-[#E8DCCB] bg-[#FFF3F8] p-4">
          <img src={`${API_BASE}/organizer/logo?auth=${getToken()}&v=${encodeURIComponent(logo.uploaded_at)}${ideaId ? `&idea=${ideaId}` : ""}`} alt="Your logo"
            className="w-24 h-24 object-contain rounded-lg bg-[#FFFDF7] border border-[#E8DCCB] p-2" data-testid="logo-preview" />
          <div className="flex-1 min-w-[160px]">
            <p className="text-sm font-bold truncate" data-testid="logo-filename">{logo.filename}</p>
            <p className="f-mono text-[10px] text-[#8C7562] mt-1">{(logo.size / 1024).toFixed(0)} KB · {logo.content_type.replace("image/", "").toUpperCase()}</p>
            <div className="flex gap-2 mt-3">
              <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="btn-ghost !py-1.5 !px-3.5 !text-[11px]" data-testid="logo-replace-btn">
                <RefreshCw size={12} /> Replace
              </button>
              <button type="button" onClick={remove} disabled={busy} className="btn-ghost !py-1.5 !px-3.5 !text-[11px] hover:!border-[#C48A9F]" data-testid="logo-remove-btn">
                <Trash2 size={12} /> Remove
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="logo-upload-btn"
          className="w-full rounded-xl border-2 border-dashed border-[#E8DCCB] bg-[#FFFDF7] hover:border-[#D4A0B4] hover:bg-[#FFF3F8] transition-colors p-7 flex flex-col items-center gap-2 text-center disabled:opacity-60">
          <span className="w-11 h-11 rounded-xl bg-[#FFF3F8] border border-[#E8DCCB] flex items-center justify-center">
            <ImagePlus size={18} className="text-[#A8785A]" />
          </span>
          <span className="text-sm font-bold">{busy ? "Uploading…" : "Upload your logo"}</span>
          <span className="text-xs text-[#8C7562]">PNG, JPG, SVG or WEBP · up to 5 MB</span>
        </button>
      )}
    </div>
  );
}
